import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Typography, Button, Space, Checkbox, Tag, Input, Result, Skeleton, Statistic, Switch, Tooltip } from 'antd'
import { Form, Field } from 'react-final-form'
import { DollarOutlined, CalendarOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import FileUploadContainer from '../FileUploadContainer'
import GridManager from './GridManager'
import { REQUIRED_COLUMNS_BY_OVERRIDE_TYPE, OPTIONAL_COLUMNS } from '../constants/Validation'
import '../styles/CreateOverride.scss'
import '../styles/OverridesShared.scss'
import LoadingSpinner from './LoadingSpinner'
import CreateOverrideStepper from './CreateOverrideStepper'
import FeeManagementHeader from './FeeManagementHeader'
import { OVERRIDE_TYPE_CONFIG } from '../constants/Breadcrumbs'
import { logFormattedMessage } from '../../../common/utils/consoleLogging'

const { Text } = Typography

const CreateOverride = ({ overrideProps, fileHandlingProps, formProps, stepperProps, gridProps }) => {
  const { title, type: overrideType, onBack } = overrideProps
  const { onFileUpload, fileName, isLoading, validationErrors, validationSummary, validationConfig } = fileHandlingProps // eslint-disable-line no-unused-vars
  const { fieldsToOverride, overrideName, handleConfigurationSubmit } = formProps
  const { currentStep, steps, goToStep, completedSteps } = stepperProps
  const { gridData, onSave } = gridProps
  const typeConfig = OVERRIDE_TYPE_CONFIG[overrideType]

  const { requiredColumns, optionalColumns } = useMemo(() => {
    return {
      requiredColumns: [
        ...REQUIRED_COLUMNS_BY_OVERRIDE_TYPE.common,
        ...(REQUIRED_COLUMNS_BY_OVERRIDE_TYPE[overrideType] || []),
      ],
      optionalColumns: OPTIONAL_COLUMNS,
    }
  }, [overrideType])

  const StepContent = ({ children }) => (
    <div className="step-content-layout override-card-base">
      <div className="step-sidebar">
        <CreateOverrideStepper
          vertical
          currentStep={currentStep}
          steps={steps}
          onStepClick={goToStep}
          completedSteps={completedSteps}
        />
      </div>
      <div className="step-divider" />
      <div className="step-main-content">{children}</div>
    </div>
  )

  const renderFieldTag = (field) => {
    const isOverrideField = field === 'Fee' || field === 'Due Date'

    return (
      <Tag key={field} className={`override-tag required-tag ${isOverrideField ? 'override-field' : ''}`}>
        {field === 'Fee' && <DollarOutlined />}
        {field === 'Due Date' && <CalendarOutlined />}
        {field}
      </Tag>
    )
  }

  const renderConfigurationStep = () => (
    <StepContent>
      <div className="configuration-content">
        <div className="configuration-header">
          <h2>Configure Override</h2>
          <Text type="secondary">
            Set up your override by providing a name and selecting which fields to override. Then upload your CSV file
            containing the override data.
          </Text>
        </div>
        <Form
          onSubmit={handleConfigurationSubmit}
          initialValues={{
            overrideName: overrideName || '',
            fieldsToOverride: fieldsToOverride || [],
          }}
          subscription={{
            submitting: true,
            pristine: true,
            values: true,
            active: true,
          }}
          validate={(values) => {
            const errors = {}
            if (!values?.overrideName?.trim()) {
              errors.overrideName = 'Please enter an override name'
            }
            if (!values?.fieldsToOverride?.length) {
              errors.fieldsToOverride = 'Please select at least one product attribute to override'
            }
            return errors
          }}
          render={({ form, values = {}, handleSubmit }) => {
            logFormattedMessage('Form render', {
              values,
              fileName,
            })

            const canProceedToDataReview = !!(
              values?.overrideName?.trim() &&
              values?.fieldsToOverride?.length > 0 &&
              fileName
            )

            return (
              <form onSubmit={handleSubmit}>
                <div className="configuration-sections">
                  <div className="form-section">
                    <div className="form-item">
                      <div className="section-title">
                        Override Name<span className="required">*</span>
                      </div>
                      <Field name="overrideName">
                        {({ input, meta }) => (
                          <>
                            <Input {...input} placeholder="Enter a name for this override" />
                            {meta.error && meta.touched && <div className="error-text">{meta.error}</div>}
                          </>
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="section-header">
                      <h3>Override Fields</h3>
                      <Text type="secondary">Select which fields you want to override</Text>
                    </div>
                    <div className="form-item">
                      <Field name="fieldsToOverride">
                        {({ input, meta }) => (
                          <>
                            <Checkbox.Group
                              className="override-checkbox-group"
                              style={{ display: 'flex', flexDirection: 'column' }}
                              options={optionalColumns.map((field) => ({
                                label: <span style={{ display: 'flex', alignItems: 'center' }}>{field}</span>,
                                value: field,
                              }))}
                              value={input.value}
                              onChange={input.onChange}
                            />
                            {meta.error && meta.touched && <div className="error-text">{meta.error}</div>}
                          </>
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="section-header">
                      <h3>Required Columns</h3>
                      <Text type="secondary">Your CSV file must include these columns</Text>
                    </div>
                    <div className="form-item">
                      <Space size={[0, 8]} wrap>
                        {[...requiredColumns, ...(values?.fieldsToOverride || [])].map(renderFieldTag)}
                      </Space>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="section-header">
                      <h3>File Upload</h3>
                      <Text type="secondary">Upload your CSV file with the override data</Text>
                    </div>
                    <div className="form-item">
                      <FileUploadContainer
                        onFileUpload={(rows, metadata) => {
                          logFormattedMessage('Before file upload', {
                            currentValues: values,
                          })
                          onFileUpload(rows, metadata, values)
                        }}
                        disabled={!values?.fieldsToOverride?.length}
                      />
                      <div className="upload-help-text">
                        <Text type="secondary">
                          {values?.fieldsToOverride?.length === 0
                            ? 'Select at least one field to override before uploading a file'
                            : fileName
                              ? `${fileName} uploaded successfully`
                              : 'Upload a CSV file containing your override data'}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="step-actions">
                  <Space>
                    <Button onClick={onBack}>Cancel</Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        if (!canProceedToDataReview) {
                          return
                        }
                        handleSubmit(values)
                      }}
                      disabled={!canProceedToDataReview}
                    >
                      Next
                    </Button>
                  </Space>
                </div>
              </form>
            )
          }}
        />
      </div>
    </StepContent>
  )

  const renderDataReviewStep = () => (
    <StepContent>
      <div className="create-override-grid-layout">
        <div className="metadata-section">
          <div className="metadata-instructions">
            <h2>Review Override Data</h2>
            <Text type="secondary">
              Review your override data below. Fix any validation errors before saving. You can use the grid controls on
              the right to filter and validate your data.
            </Text>
          </div>
          <div className="details-grid">
            {isLoading ? (
              <div className="skeleton-row">
                <Skeleton active paragraph={{ rows: 1 }} />
                <Skeleton active paragraph={{ rows: 1 }} />
              </div>
            ) : (
              <>
                <Statistic title="Override Name" value={overrideName} />
                <div className="status-item">
                  <Text className="status-label">Status</Text>
                  <Space align="center" size={12}>
                    <Tooltip title="Overrides are inactive when first created">
                      <Switch
                        checked={false}
                        disabled
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                      />
                    </Tooltip>
                    <Text strong className="status-inactive">
                      Inactive
                    </Text>
                  </Space>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="grid-section">
          {isLoading ? (
            <div className="loading-container">
              <LoadingSpinner text="Loading data..." />
            </div>
          ) : (
            <GridManager
              initialRowData={gridData}
              validationSummary={validationSummary}
              validationConfig={validationConfig}
              onSave={onSave}
              totalErrors={validationSummary?.totalErrorCount || 0}
            />
          )}
        </div>
      </div>
    </StepContent>
  )

  const renderSummaryStep = () => (
    <StepContent>
      <Result
        status="success"
        title="Override Created Successfully"
        subTitle={`Your ${title.toLowerCase()} has been created.`}
        extra={[
          <Button type="primary" key="done" onClick={onBack}>
            Done
          </Button>,
        ]}
      />
    </StepContent>
  )

  const stepContent = {
    0: renderConfigurationStep,
    1: renderDataReviewStep,
    2: renderSummaryStep,
  }

  return (
    <div className="create-override fee-management-container">
      <FeeManagementHeader
        title={`Create ${title}`}
        icon={typeConfig.icon}
        onBack={onBack}
        breadcrumbs={[
          {
            text: typeConfig.title,
            path: `/fee-management/overrides/${overrideType}`,
            icon: typeConfig.icon,
          },
          {
            text: 'Create Override',
            path: `/fee-management/overrides/${overrideType}/create`,
          },
        ]}
      />

      <div className="create-override-content">{stepContent[currentStep]()}</div>
    </div>
  )
}

CreateOverride.propTypes = {
  overrideProps: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['location', 'vendor', 'vendorLocation']).isRequired,
    onBack: PropTypes.func.isRequired,
  }).isRequired,

  fileHandlingProps: PropTypes.shape({
    onFileUpload: PropTypes.func.isRequired,
    fileName: PropTypes.string.isRequired,
    validationErrors: PropTypes.array.isRequired,
    validationSummary: PropTypes.object,
    validationConfig: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,

  formProps: PropTypes.shape({
    fieldsToOverride: PropTypes.arrayOf(PropTypes.string).isRequired,
    overrideName: PropTypes.string.isRequired,
    handleConfigurationSubmit: PropTypes.func.isRequired,
    hasCompletedConfiguration: PropTypes.bool.isRequired,
  }).isRequired,

  stepperProps: PropTypes.shape({
    currentStep: PropTypes.number.isRequired,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        icon: PropTypes.elementType.isRequired,
      }),
    ).isRequired,
    goToStep: PropTypes.func.isRequired,
    completedSteps: PropTypes.objectOf(PropTypes.bool).isRequired,
  }).isRequired,

  gridProps: PropTypes.shape({
    gridData: PropTypes.array,
    onSave: PropTypes.func.isRequired,
  }).isRequired,
}

export default CreateOverride
